import { z } from "zod";

// Comparisons: one NAME.compare file asks one question about two or more agents (arms). Each arm runs the same inputs,
// each run is measured the same way, and a fixed decision rule gives one answer: an arm is better, or can't tell yet.
// The CLI runs the cells on a person's computer and reports them with the /api/cli/comparisons routes; the dashboard
// reads them with /api/comparisons. Runs are ordinary synced runs (PUT /api/cli/runs/:id). In account mode a run cell's
// transcript.jsonl and a grade's "what the grader saw" go up as result files (PUT /api/cli/result-files/:hash) and are
// named by hash when the cell is linked. When run content stays on computers, only numbers sync, and the dashboard reads
// the transcript and verdicts from the local Method server (LOCAL_METHOD_SERVER).

export const ComparisonIdSchema = z.string().regex(/^cmp_[a-f0-9]{32}$/);
const Name = z.string().trim().min(1);
const ArmIdSchema = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/);
const KeySchema = z.string().regex(/^[a-f0-9]{64}$/);

/** A file that the CLI uploaded first as a result file (PUT /api/cli/result-files/:hash). */
const ResultFileRefSchema = z.strictObject({ sha256: KeySchema, bytes: z.number().int().min(0).max(25_000_000) });
const Text = (max: number) => z.string().trim().min(1).max(max);

/** Who decided a value: you (the person said it), writer (the agent that wrote the file chose it), method (a built-in rule). */
export const DeciderSchema = z.enum(["you", "writer", "method"]);
export type Decider = z.output<typeof DeciderSchema>;
/**
 * The parts of a comparison, as the page shows them. Notes, decisions and Method's rules each name one.
 * same_for_both: what every arm shares. noise: the noise check of the decision rule. run_rules: failures, reuse and who can see runs.
 */
export const ComparisonPartSchema = z.enum([
  "arms", "same_for_both", "inputs", "treatment", "main_measure", "other_measures", "runs_per_point", "rule", "noise", "run_rules", "trial", "assumptions",
]);
export type ComparisonPart = z.output<typeof ComparisonPartSchema>;
/**
 * Who decided one value of the file. what names the value in its part:
 * - arms: "difference", "instructions" (the starting instructions), "other differences", "tested" (model or tools);
 * - same_for_both: "deliverable" (the file that each arm hands in; Method's rule for it names the file on the page);
 * - inputs: "inputs", "teaching inputs";
 * - treatment: "treatment" (count, order, seed and checkpoints);
 * - main_measure: "main measure"; other_measures: the measure's name;
 * - runs_per_point: "learners"; rule: "decision rule";
 * - assumptions: the assumption's text;
 * - any part: "note", with the note's text as value.
 * value is the value as the file gives it, in words. A value with no decision is the writer's. entries: the decided_by_you
 * entries of the file that make the value the person's.
 */
export const DecisionRecordSchema = z.strictObject({
  part: ComparisonPartSchema, what: Text(200), value: z.string().max(2000), by: DeciderSchema, entries: z.array(Text(200)).max(20).optional(),
});
export type DecisionRecord = z.output<typeof DecisionRecordSchema>;
/** A rule that Method applies to every comparison, in one sentence, with the part it belongs to. */
export const MethodRuleSchema = z.strictObject({ part: ComparisonPartSchema, text: Text(1000) });
export type MethodRule = z.output<typeof MethodRuleSchema>;

/** An agent under test: a Method (its own Method ID) or a plain Claude Code or Codex session. instructions: its starting instructions, in full. */
export const ArmSchema = z.strictObject({
  id: ArmIdSchema,
  name: Name.max(120),
  method_id: z.string().regex(/^wf_[a-f0-9]{32}$/).optional(),
  agent: z.enum(["claude", "codex"]).optional(),
  instructions: z.string().max(100_000).optional(),
});
/**
 * How a measure is found: code, a model grader with a written rubric, a person, or usage (Method counts it).
 * description: what the number is, in plain words. checklists: the hidden checklist of each input, by input ID.
 */
export const MeasureSchema = z.strictObject({
  name: Name.max(120),
  how: z.enum(["code", "model", "person", "usage"]),
  description: Text(1000).optional(),
  /** false when a lower number is better, such as words or minutes. */
  higher_is_better: z.boolean().optional(),
  rubric: z.string().max(20_000).optional(),
  checklists: z.record(Name.max(200), z.array(Text(2000)).max(200)).optional(),
});
/** The fixed decision rule: margin on the main measure, minimum runs per point, the deciding checkpoint, and a guard. */
export const DecisionRuleSchema = z.strictObject({
  margin: z.number().positive(),
  min_runs: z.number().int().min(1),
  at: z.number().int().min(0).optional(),
  guard: z.strictObject({ measure: Name.max(120), max_drop: z.number().min(0) }).optional(),
});
/**
 * Something that must be true for the answer to mean something. if_false: what the answer would mean then. check: how
 * to check it, in words. limit: a known limit, which has neither. checked_by: Method checks it itself, from what each
 * arm received (treatment) or from people's marks on the grader's verdicts (grader_marks). Files before notes and
 * marks sent check as treatment, grader_marks or null; they read the same.
 */
export const AssumptionSchema = z.strictObject({
  text: Name.max(500),
  if_false: Text(500).optional(),
  check: z.string().trim().max(500).nullable().optional(),
  by: DeciderSchema.optional(),
  limit: z.boolean().optional(),
  checked_by: z.enum(["treatment", "grader_marks"]).optional(),
});
export type Assumption = z.output<typeof AssumptionSchema>;
/** How Method checks an assumption itself: checked_by, or an older file's check of treatment or grader_marks. */
export const automaticCheck = (assumption: Assumption) =>
  assumption.checked_by ?? (assumption.check === "treatment" || assumption.check === "grader_marks" ? assumption.check : undefined);

/**
 * A question that the writer of the file asks on the page: what it shows first, in order (the writer's sentences, said,
 * and the real thing word for word, quote: a correction, a label, the text of a file), the one direct question (ask),
 * what a "no" means for the result (if_no), and the member who answers it (for; else the person who made the comparison).
 */
export const DesignQuestionSchema = z.strictObject({
  show: z.array(z.union([z.strictObject({ said: Text(1000) }), z.strictObject({ quote: Text(8000) })])).min(1).max(20),
  ask: Text(1000), if_no: Text(1000), for: Name.max(200).optional(),
});
export type DesignQuestion = z.output<typeof DesignQuestionSchema>;
export type Shown = DesignQuestion["show"][number];

/** One input by name, and its content when the CLI uploaded it. */
export const InputDetailSchema = z.strictObject({ name: Name.max(200), file: ResultFileRefSchema.optional() });

const ExhibitIdSchema = z.string().regex(/^[a-z0-9-]{1,120}$/);
/** One verbatim text of an exhibit: a file by its path, a prompt, a chat message, or what a script printed. anchor: a label can link to it. */
export const ExhibitBlockSchema = z.strictObject({ name: Text(300), text: z.string().max(100_200), anchor: ExhibitIdSchema.optional() });
/**
 * An exhibit: a literal thing of the comparison, which the page shows verbatim. Method makes each one from the comparison's
 * own files and code, with the run's own functions, so the writer cannot paraphrase it. caption: one line saying where it
 * comes from. arm: the agent it belongs to. replaces: on a trial example, the comparison's exhibit that it shows from a
 * real run (correction, output).
 */
export const ExhibitSchema = z.strictObject({
  id: ExhibitIdSchema, title: Text(300), caption: Text(1000), arm: ArmIdSchema.optional(), blocks: z.array(ExhibitBlockSchema).max(80), replaces: ExhibitIdSchema.optional(),
});
export type Exhibit = z.output<typeof ExhibitSchema>;
/**
 * The literal things that the page shows first. see: what the agents see (what each receives, the files in its folder,
 * one correction as each gets it, the handed-in file). scored: how a run is scored (the hidden answer, each measure on a
 * sample). labels: each word that the page uses as a label, with the exhibit (or block anchor) that shows it.
 */
export const ExhibitsSchema = z.strictObject({
  see: z.array(ExhibitSchema).max(40), scored: z.array(ExhibitSchema).max(40), labels: z.record(Text(120), ExhibitIdSchema),
});
export type Exhibits = z.output<typeof ExhibitsSchema>;

/** The parts of a comparison file. Method refuses a file without the required parts. */
export const ComparisonSpecSchema = z.strictObject({
  question: Name.max(500),
  arms: z.array(ArmSchema).min(2).max(8),
  /** The one thing that is different between the arms. */
  difference: Name.max(500),
  /** Any other difference, written down. */
  other_differences: z.array(Name.max(500)).max(20).optional(),
  /**
   * checking: the input IDs that are measured; teaching: the ones a treatment is given on. description and
   * teaching_description say what they are in one sentence. noun: what one input is, such as "task". details: each
   * input's name and content, by input ID.
   */
  inputs: z.strictObject({
    checking: z.array(Name.max(200)).min(1).max(1000), teaching: z.array(Name.max(200)).max(1000).optional(),
    description: Text(1000).optional(), teaching_description: Text(1000).optional(), noun: Text(40).optional(),
    details: z.record(Name.max(200), InputDetailSchema).optional(),
  }),
  main_measure: MeasureSchema,
  /** Shown, never deciding. Method adds a usage measure. */
  other_measures: z.array(MeasureSchema).max(20).optional(),
  rule: DecisionRuleSchema,
  runs_per_point: z.number().int().min(1).max(100),
  /** Separate learners per arm: each gets the whole treatment. The runs per point count them. */
  learners: z.number().int().min(1).max(100).optional(),
  /** Inputs per arm in the trial. 2 when the file does not say. */
  trial: z.number().int().min(1).max(100).optional(),
  /**
   * Events that each arm gets one at a time, such as corrections, with checkpoints where each arm is measured. order:
   * given, or shuffled once with seed (the same order for every arm). list: each item's input and words, in that order,
   * and where the words come from (source), such as "her reply of 22 Oct, word for word" or "written by us".
   */
  treatment: z.strictObject({
    kind: Name.max(80), items: z.number().int().min(1).max(10_000), checkpoints: z.array(z.number().int().min(0)).min(1).max(100),
    order: z.enum(["given", "shuffled"]).optional(), seed: z.number().int().optional(),
    list: z.array(z.strictObject({ input: Name.max(200), text: Text(4000), source: Text(2000).optional() })).max(2000).optional(),
  }).optional(),
  expected_answers: z.boolean().optional(),
  /** The one judge of model measures. */
  judge: z.strictObject({ agent: z.enum(["claude", "codex"]), model: Name.max(120) }).optional(),
  assumptions: z.array(AssumptionSchema).max(20).optional(),
  /** Notes that go with a part, in words. */
  notes: z.record(ComparisonPartSchema, z.array(Text(1000)).max(20)).optional(),
  /** Who decided each value of the file. */
  decisions: z.array(DecisionRecordSchema).max(500).optional(),
  /** The rules that Method applies to every comparison, by part. */
  method_rules: z.array(MethodRuleSchema).max(100).optional(),
  /** The literal things that the page shows first. */
  exhibits: ExhibitsSchema.optional(),
  /** The questions that the page asks, 7 or fewer. */
  questions: z.array(DesignQuestionSchema).min(1).max(7).optional(),
}).superRefine((spec, context) => {
  const ids = spec.arms.map(arm => arm.id), methods = spec.arms.flatMap(arm => arm.method_id ? [arm.method_id] : []);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: "custom", path: ["arms"], message: "Give each arm its own ID." });
  if (new Set(methods).size !== methods.length) context.addIssue({ code: "custom", path: ["arms"], message: "Two arms share a Method ID. Give each Method arm its own Method." });
  if (spec.rule.at !== undefined && !spec.treatment?.checkpoints.includes(spec.rule.at))
    context.addIssue({ code: "custom", path: ["rule", "at"], message: "The rule's checkpoint must be one of the treatment's checkpoints." });
  if (spec.treatment && spec.rule.at === undefined) context.addIssue({ code: "custom", path: ["rule", "at"], message: "Name the checkpoint that decides." });
  if (spec.rule.guard && !spec.other_measures?.some(measure => measure.name === spec.rule.guard!.measure))
    context.addIssue({ code: "custom", path: ["rule", "guard"], message: "The guard must name one of the other measures." });
});
export type ComparisonSpec = z.output<typeof ComparisonSpecSchema>;

/** The name of each part, as the version bar names a changed part. */
export const COMPARISON_PARTS: Record<keyof ComparisonSpec, string> = {
  question: "Question", arms: "Arms", difference: "Arms", other_differences: "Arms", inputs: "Inputs", main_measure: "Main measure",
  other_measures: "Other measures", rule: "Decision rule", runs_per_point: "Runs per point", learners: "Runs per point", trial: "Trial size",
  treatment: "Treatment over time", expected_answers: "Expected answers", judge: "Main measure", assumptions: "Assumptions", notes: "Notes",
  decisions: "Who decided", method_rules: "Method's rules", exhibits: "Exactly what the agents see", questions: "Questions",
};
/** The parts that differ between two versions of a file, in the file's order, each once. */
export function changedParts(before: ComparisonSpec, after: ComparisonSpec) {
  const parts = (Object.keys(COMPARISON_PARTS) as (keyof ComparisonSpec)[])
    .filter(key => JSON.stringify(before[key] ?? null) !== JSON.stringify(after[key] ?? null)).map(key => COMPARISON_PARTS[key]);
  return [...new Set(parts)];
}

/** Who decided a value of the file: its decision, else the writer. */
export function decidedBy(spec: Pick<ComparisonSpec, "decisions">, part: ComparisonPart, what: string): Decider {
  return spec.decisions?.find(decision => decision.part === part && decision.what === what)?.by ?? "writer";
}

/** A number at the precision of the rule: at least two places below 1, as 0.10. */
function places(value: number) {
  const text = String(value), decimals = text.includes(".") ? text.split(".")[1]!.length : 0;
  return Math.abs(value) < 1 ? Math.max(2, decimals) : decimals;
}
const fixed = (value: number, digits: number) => value.toFixed(digits);
const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);
/** An input's name: its name in the file, else its ID without folder and extension. */
export function inputName(spec: Pick<ComparisonSpec, "inputs">, id: string) {
  return spec.inputs.details?.[id]?.name ?? id.replace(/^.*[/\\]/, "").replace(/\.[a-z0-9]+$/i, "");
}
/** What one input is ("task"), and its plural. */
export function inputNoun(spec: Pick<ComparisonSpec, "inputs">, count: number) {
  const one = spec.inputs.noun ?? "input";
  return count === 1 ? one : one.endsWith("s") ? one : `${one}s`;
}

/** Words that start a sentence only as common words: after "If this is false," they take a small letter. Names keep theirs. */
const COMMON = new Set(["a", "an", "the", "this", "that", "these", "those", "it", "its", "they", "their", "there", "then", "we", "our", "you", "your",
  "he", "she", "his", "her", "each", "every", "all", "any", "some", "no", "none", "one", "both", "either", "neither", "if", "when", "most", "more",
  "many", "few", "other", "another", "only", "at", "in", "on", "for", "by", "with", "from", "after", "before", "as", "not", "what", "which", "how",
  "read", "check", "ask", "compare", "count", "look", "run", "mark", "see", "open", "give", "test", "review", "use", "make", "let", "keep"]);
/** A text that continues a sentence after a comma: "If this is false, the scores…", but "To check it, Gráinne marks…". */
export function continued(text: string) {
  const word = /^[A-Za-z]+/.exec(text)?.[0];
  return word && COMMON.has(word.toLowerCase()) && (word.length === 1 || word !== word.toUpperCase()) ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}
/** A text as one sentence: one period at its end, never two. */
export const sentence = (text: string) => `${text.trim().replace(/[.\s]+$/, "")}.`;

/**
 * The writer's words with each file or folder path said by its name: "corrections/corrections.json" is "corrections",
 * "steps/locate.mjs" is "locate", "expected/" is "expected". The page names files; it never shows a path.
 */
export function withoutPaths(text: string) {
  return text
    .replace(/(?<![\w/.:@-])(?:\.{1,2}\/)?(?:[\w.{}-]+\/)+([\w{}-]+(?:\.[\w-]+)*)\.[A-Za-z][A-Za-z0-9]{0,4}\b/g, (_path, name: string) => name)
    .replace(/(?<![\w/.:@-])(?:\.{1,2}\/)?(?:[\w.-]+\/)*([\w.-]+)\/(?=[\s,.;:)!?]|$)/g, (_path, name: string) => name);
}
/**
 * The correction that a line of what an agent missed names first: "Correction 3 (“…”) was not received: …" or
 * "Copy 2: correction 3 (“…”) …", as the CLI writes it. The page opens its full words from the treatment list.
 */
export function missedCorrection(line: string): number | null {
  const found = /^(?:Copy \d+: )?[Cc]orrection (\d+) \(“/.exec(line);
  return found ? Number(found[1]) : null;
}

/**
 * The too easy check in one sentence: "This test is too easy to show a difference: both agents already score 0.95
 * (Method agent) and 0.97 (plain Claude Code) before any correction, and the rule needs a lead of 0.10." when it stops,
 * else that each agent can still lead.
 */
export function tooEasySentence(spec: Pick<ComparisonSpec, "arms" | "treatment">, check: TooEasy) {
  const digits = (value: number) => Math.abs(value) < 1 ? value.toFixed(2) : String(value);
  const arms = spec.arms.filter(arm => check.start[arm.id] !== undefined), values = arms.map(arm => `${digits(check.start[arm.id]!)} (${arm.name})`);
  const list = values.length > 1 ? `${values.slice(0, -1).join(", ")} and ${values.at(-1)}` : values.join("");
  const when = spec.treatment ? (spec.treatment.kind === "New inputs" ? " at the start" : " before any correction") : "";
  if (!check.passes) return `This test is too easy to show a difference: ${arms.length === 2 ? "both agents" : `all ${arms.length} agents`} already score ${list}${when}, and the rule needs a lead of ${digits(check.margin)}.`;
  return `The agents score ${list}${when}, so an agent can still lead by the ${digits(check.margin)} margin.`;
}

/**
 * Why a trial stopped, in plain words, or null when it did not: an agent did not receive everything (with the first
 * thing it misses and why), or the test is too easy (every agent already scores above 1 − margin at the start).
 */
export function trialStop(view: Pick<ComparisonView, "spec" | "trial">): string | null {
  const trial = view.trial;
  if (!trial) return null;
  for (const arm of view.spec.arms) {
    const short = trial.received[arm.id]?.lines.find(line => line.received < line.planned);
    if (short) {
      const what = lower(short.what.replace(/ (received|run)$/i, ""));
      return `${arm.name} received ${short.received} of ${short.planned} ${what}.${short.missing[0] ? ` ${sentence(short.missing[0])}` : ""}`;
    }
  }
  if (trial.too_easy && !trial.too_easy.passes) return lower(tooEasySentence(view.spec, trial.too_easy));
  return null;
}

/**
 * The decision rule as one sentence, starting in lower case so it can follow "Decision rule:":
 * "an agent wins if it is at least 0.10 ahead on corrected items after 30 corrections, and further ahead than two copies
 * of one agent differ there, with at least 2 copies, and no more than 0.05 behind on all items. Otherwise: can't tell yet."
 */
export function ruleSentence(spec: ComparisonSpec) {
  const { rule, main_measure: main } = spec, digits = places(rule.margin);
  const ahead = main.higher_is_better === false ? "lower" : "ahead";
  const others = spec.arms.length > 2 ? (ahead === "ahead" ? " of every other agent" : " than every other agent") : "";
  const at = rule.at !== undefined && spec.treatment ? ` after ${rule.at} ${lower(spec.treatment.kind)}` : "";
  // A winner needs two copies of each agent: with one, nothing shows how much copies differ.
  const least = spec.learners ? Math.max(2, rule.min_runs) : rule.min_runs;
  const runs = `${least} ${spec.learners ? "copies" : least === 1 ? "run per point" : "runs per point"}`;
  const guard = rule.guard ? `, and no more than ${fixed(rule.guard.max_drop, places(rule.guard.max_drop))} behind on ${lower(rule.guard.measure)}` : "";
  return `an agent wins if it is at least ${fixed(rule.margin, digits)} ${ahead}${others} on ${lower(main.name)}${at}, and further ${ahead} than two copies of one agent differ there, with at least ${runs}${guard}. Otherwise: can't tell yet.`;
}
/**
 * Two worked examples of the rule with its own numbers, for the first two arms: one where the first arm wins and one
 * where nobody can tell yet. "Method agent 0.80, Plain Claude Code 0.65 → Method agent wins."
 */
export function ruleExamples(spec: ComparisonSpec) {
  const { margin } = spec.rule, digits = places(margin), up = spec.main_measure.higher_is_better !== false;
  const [first, second] = spec.arms;
  const base = margin < 1 ? (up ? 0.8 : 0.2) : Math.round(margin * (up ? 8 : 2));
  const other = (times: number) => up ? base - margin * times : base + margin * times;
  const line = (value: number, wins: boolean) =>
    `${first!.name} ${fixed(base, digits)}, ${second!.name} ${fixed(value, digits)} → ${wins ? `${first!.name} wins` : "can't tell yet"}.`;
  return [line(other(1.5), true), line(other(0.6), false)];
}
/** "2 separate copies of each agent. Each copy gets all 30 corrections and does all 4 tasks at each checkpoint: 8 graded runs per agent per checkpoint, and 112 in all." */
export function runsSentence(spec: ComparisonSpec) {
  const inputs = spec.inputs.checking.length, noun = inputNoun(spec, inputs), arms = spec.arms.length;
  const points = spec.treatment?.checkpoints.length ?? 1;
  if (spec.learners) {
    const per = spec.learners * inputs;
    const treatment = spec.treatment ? ` Each copy gets all ${spec.treatment.items} ${lower(spec.treatment.kind)} and does all ${inputs} ${noun} at each checkpoint:` : ` Each copy does all ${inputs} ${noun}:`;
    return `${spec.learners === 1 ? "1 copy" : `${spec.learners} separate copies`} of each agent.${treatment} ${per} graded ${per === 1 ? "run" : "runs"} per agent${spec.treatment ? " per checkpoint" : ""}, and ${per * arms * points} in all.`;
  }
  const per = spec.runs_per_point * inputs;
  return `${spec.runs_per_point} ${spec.runs_per_point === 1 ? "run" : "runs"} of each agent on each of the ${inputs} ${noun}${spec.treatment ? " at each checkpoint" : ""}: ${per} graded ${per === 1 ? "run" : "runs"} per agent${spec.treatment ? " per checkpoint" : ""}, and ${per * arms * points} in all.`;
}
/** "30, one at a time, in one shuffled order (seed 2610), the same for both agents. The agents are measured after 0, 15 and 30 corrections." */
export function treatmentSentence(spec: ComparisonSpec) {
  const treatment = spec.treatment;
  if (!treatment) return null;
  const both = spec.arms.length === 2 ? "both agents" : "every agent";
  const order = treatment.order === "shuffled" ? `in one shuffled order${treatment.seed !== undefined ? ` (seed ${treatment.seed})` : ""}` : "in the given order";
  const points = [...treatment.checkpoints].sort((a, b) => a - b);
  const list = (values: number[]) => values.length === 1 ? String(values[0]) : `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`;
  return `${treatment.items}, one at a time, ${order}, the same for ${both}. The agents are measured after ${list(points)} ${lower(treatment.kind)}.`;
}

/** One cell that a version needs: a run of one arm on one input, or a grade of a run cell by one grader version. */
export const PlannedCellSchema = z.strictObject({
  key: KeySchema,
  kind: z.enum(["run", "grade"]),
  arm: ArmIdSchema,
  input: Name.max(200),
  /** The treatment checkpoint the run measures, or null without treatment over time. */
  checkpoint: z.number().int().min(0).nullable(),
  repeat: z.number().int().min(0),
  /** Part of the trial. Trial cells are reused by the full run. */
  trial: z.boolean(),
  /** A grade: the run cell it grades, and the grader's version (a content hash). */
  target: KeySchema.optional(),
  grader_version: z.string().min(1).max(100).optional(),
}).superRefine((cell, context) => {
  if ((cell.kind === "grade") !== (cell.target !== undefined && cell.grader_version !== undefined))
    context.addIssue({ code: "custom", message: "A grade cell names its target and grader version; a run cell names neither." });
});
export type PlannedCell = z.output<typeof PlannedCellSchema>;
export const MAX_PLANNED_CELLS = 5000;
/** The Method that runs a comparison on the person's computer (its comparison.method), once it is saved in the account. */
export const ComparisonMethodIdSchema = z.string().regex(/^wf_[a-f0-9]{32}$/);
export const ComparisonPlanSchema = z.strictObject({ cells: z.array(PlannedCellSchema).min(1).max(MAX_PLANNED_CELLS), method_id: ComparisonMethodIdSchema.optional() });

/** One grader verdict, anchored to the transcript line it judged (line: the entry's i), with the words it quoted. */
export const GraderVerdictSchema = z.strictObject({
  criterion: Name.max(2000),
  verdict: z.enum(["met", "partly", "not_met"]),
  quote: z.string().max(4000).nullable(),
  line: z.number().int().min(0).nullable(),
});
export type GraderVerdict = z.output<typeof GraderVerdictSchema>;
/**
 * A finished cell. run_id: the computer's run ID of a run cell (the run is synced first). measures: a grade's number per
 * measure name. Content (account mode only): treatment_note, the exact treatment the run received; transcript, the
 * run's transcript.jsonl; seen, the text the grader read; verdicts, the grade's verdicts.
 */
export const CellLinkSchema = z.strictObject({
  key: KeySchema,
  run_id: z.string().uuid().optional(),
  measures: z.record(z.string().min(1).max(120), z.number().finite()).optional(),
  treatment_note: z.string().max(100_000).optional(),
  transcript: ResultFileRefSchema.optional(),
  seen: ResultFileRefSchema.optional(),
  verdicts: z.array(GraderVerdictSchema).max(200).optional(),
});
export const CellLinksSchema = z.strictObject({ cells: z.array(CellLinkSchema).min(1).max(200) });

/**
 * One kind of thing that an arm received: "Corrections received: 5 of 5", "Inputs run: 2 of 2". missing names each item
 * it did not get. changed: of the corrections received, those that changed the agent (a tested change applied, or the
 * instructions edited).
 */
export const ReceivedLineSchema = z.strictObject({
  what: Name.max(80), received: z.number().int().min(0), planned: z.number().int().min(0), missing: z.array(z.string().max(500)).max(200),
  changed: z.number().int().min(0).optional(),
});
/**
 * What happened to one treatment item in one learner. A Method arm: fixed, no_change_needed (it already did it),
 * no_fix_found or could_not_test (rule_added: a learned rule added without a test, in comparisons run before the
 * learner was one agent session). A plain arm: edited (its instructions changed) or not_edited. Either arm: was_right, when
 * a feedback writer found nothing wrong with the arm's own output and the arm was told it was right; no_feedback, when the
 * writer checked only a part, found nothing wrong there, and the arm was told nothing.
 */
export const CorrectionOutcomeSchema = z.enum(["fixed", "no_change_needed", "no_fix_found", "could_not_test", "edited", "not_edited", "was_right", "no_feedback", "rule_added"]);
export type CorrectionOutcome = z.output<typeof CorrectionOutcomeSchema>;
/**
 * What a treatment item changed: for a Method arm, the learner's plain summary and the diff of the Method's files; for a
 * plain arm, the diff of its instructions. The diff is cut at 2000 characters; the computer keeps it whole.
 */
export const ChangeSchema = z.strictObject({ summary: Text(500).optional(), diff: z.string().max(2000) });
/**
 * One treatment item as one learner received it. index: its place in the order, from 1. learner: from 1. input: the
 * input ID. text: the words that this learner received (a feedback writer writes them from each learner's own output).
 * change: what it changed, when it changed the agent.
 */
export const ReceivedCorrectionSchema = z.strictObject({
  index: z.number().int().min(1), input: Name.max(200), text: Text(4000), outcome: CorrectionOutcomeSchema, learner: z.number().int().min(1),
  change: ChangeSchema.optional(),
});
/** What each arm received: the models that its runs actually used, one line per kind of thing, and each treatment item with its outcome. */
export const ReceivedSchema = z.record(ArmIdSchema, z.strictObject({
  models: z.array(z.string().min(1).max(120)).max(10), lines: z.array(ReceivedLineSchema).max(10),
  corrections: z.array(ReceivedCorrectionSchema).max(5000).optional(),
}));
export type Received = z.output<typeof ReceivedSchema>;
/**
 * The noise at the start: spread is how much two copies of the same agent differ on the main measure before any
 * treatment, margin the rule's margin, start each arm's value at the start, one per copy (null when a copy has none).
 * It is shown and stops nothing: the decision checks the spread between copies at the point that decides. passes is
 * what a CLI before 0.23.0 sends; it decides nothing.
 */
export const NoiseSchema = z.strictObject({
  spread: z.number().min(0), margin: z.number().positive(), passes: z.boolean().optional(),
  start: z.record(ArmIdSchema, z.array(z.number().nullable()).max(100)).optional(),
});
export type Noise = z.output<typeof NoiseSchema>;
/**
 * The too easy check at the start: each arm's start value on the main measure (the one the noise check uses), the
 * rule's margin, and passes: false when every arm is above 1 − margin, so no arm can lead by the margin.
 */
export const TooEasySchema = z.strictObject({
  start: z.record(ArmIdSchema, z.number()), margin: z.number().positive(), passes: z.boolean(),
});
export type TooEasy = z.output<typeof TooEasySchema>;
/**
 * The trial: inputs per arm, what each arm received, the noise at the start, the too easy check, and examples: each
 * arm's worked example from its real trial runs (the words it received, what it handed in, how one run was scored).
 */
export const TrialSchema = z.strictObject({ inputs: z.number().int().min(1), received: ReceivedSchema, noise: NoiseSchema.optional(), too_easy: TooEasySchema.optional(),
  examples: z.array(ExhibitSchema).max(40).optional() });
export type Trial = z.output<typeof TrialSchema>;
/**
 * The full run, estimated from the trial. runs: every graded run of the full run, as the runs per point count them;
 * reused: those that the trial already ran. events: the treatment items still to give over all learners, each a
 * training run and a fix or edit; event_minutes: each arm's minutes per item in the trial (null when none finished).
 * minutes: the treatment, then the runs. usage: each arm's usage per correction in the trial, in tokens or minutes.
 * After the full run, the same record holds what it took.
 */
export const CostEstimateSchema = z.strictObject({
  minutes: z.number().min(0), runs: z.number().int().min(0), usd: z.number().min(0).nullable(),
  reused: z.number().int().min(0).optional(), events: z.number().int().min(0).optional(),
  event_minutes: z.record(ArmIdSchema, z.number().min(0).nullable()).optional(),
  usage: z.strictObject({ unit: z.enum(["tokens", "minutes"]), values: z.record(ArmIdSchema, z.number().min(0).nullable()) }).optional(),
});
export type CostEstimate = z.output<typeof CostEstimateSchema>;
/** The answer of the decision rule, with its reason in one sentence and the main measure per arm at the deciding point. */
export const DecisionSchema = z.strictObject({
  answer: z.enum(["better", "cant_tell"]),
  arm: ArmIdSchema.optional(),
  reason: Name.max(500),
  values: z.record(ArmIdSchema, z.strictObject({ value: z.number().nullable(), n: z.number().int().min(0) })).optional(),
}).superRefine((decision, context) => {
  if ((decision.answer === "better") !== (decision.arm !== undefined)) context.addIssue({ code: "custom", message: "Name the arm that is better, and only then." });
});
export type Decision = z.output<typeof DecisionSchema>;
export const ComparisonStageSchema = z.enum(["draft", "trial", "running", "done"]);
export type ComparisonStage = z.output<typeof ComparisonStageSchema>;
/**
 * A stage change. trial: the trial ran (its inputs per arm, what each arm received, the noise). running: the person approved
 * and the full run started; this locks the file. done: the decision rule was applied. received, cost_estimate and
 * decision can come with any stage.
 */
export const StageUpdateSchema = z.strictObject({
  stage: z.enum(["trial", "running", "done"]),
  trial: TrialSchema.optional(),
  received: ReceivedSchema.optional(),
  cost_estimate: CostEstimateSchema.optional(),
  decision: DecisionSchema.optional(),
  method_id: ComparisonMethodIdSchema.optional(),
}).superRefine((update, context) => {
  if (update.stage === "trial" && !update.trial) context.addIssue({ code: "custom", message: "A trial reports its inputs and what each arm received." });
  if (update.stage === "done" && !update.decision) context.addIssue({ code: "custom", message: "A finished comparison reports its decision." });
});
export const SaveComparisonSchema = z.strictObject({ name: Name.max(200), spec: ComparisonSpecSchema, method_id: ComparisonMethodIdSchema.optional() });

/**
 * Questions for you: the page asks them one at a time. The writer's questions in the file (question: each shows the
 * real thing and asks one direct question; to the person who made the comparison, or to the member it names), a
 * correction whose words the writer wrote ("Would you give this correction?", to the expert), and the approvals of the
 * trial and of the full run. The answers that a question takes: no on a writer's question, and differently, take the
 * person's words.
 */
export const QuestionChoiceSchema = z.enum(["yes", "no", "differently", "not_yet"]);
export type QuestionChoice = z.output<typeof QuestionChoiceSchema>;
export type QuestionKind = "question" | "correction" | "trial" | "full_run";
/** An answer from the page. key: the question's key. */
export const QuestionAnswerSchema = z.strictObject({
  key: KeySchema, choice: QuestionChoiceSchema, words: z.string().trim().min(1).max(2000).optional(),
});

/** A person's right or wrong on one grader verdict. */
export const GraderMarkSchema = z.strictObject({
  comparison_id: ComparisonIdSchema,
  grade: KeySchema,
  verdict: z.number().int().min(0).max(199),
  mark: z.enum(["right", "wrong"]),
  note: z.string().trim().min(1).max(4000).optional(),
});

/** One line of a run's transcript.jsonl, as the runtime writes it. parent and at are read when present. */
export const TranscriptEntrySchema = z.object({
  i: z.number().int().min(0),
  role: z.enum(["user", "assistant", "tool", "system"]),
  kind: z.string(),
  text: z.string(),
  tool: z.string().nullable(),
  path: z.string().nullable(),
  raw_ref: z.string(),
  inferred: z.boolean().optional(),
  /** The i of the subagent call (Agent or Task) that made this entry. */
  parent: z.number().int().min(0).optional(),
  /** When the entry happened (ISO time). */
  at: z.string().optional(),
});
export type TranscriptEntry = z.output<typeof TranscriptEntrySchema>;
/** Each line of a transcript.jsonl that reads as an entry, in order. */
export function parseTranscript(text: string): TranscriptEntry[] {
  return text.split("\n").flatMap(line => {
    if (!line.trim()) return [];
    try { const entry = TranscriptEntrySchema.safeParse(JSON.parse(line)); return entry.success ? [entry.data] : []; } catch { return []; }
  });
}

// What the dashboard reads.

export type ComparisonSummary = {
  id: string; name: string; question: string; arms: string[]; stage: ComparisonStage; version_number: number;
  /** "Method agent wins" or "Can't tell yet". */
  answer: string; decision: Decision | null; updated_at: string;
  /** The questions on the page that the reader can answer and nobody has answered yet. */
  open_questions: number;
  run: ComparisonRun | null;
};
/**
 * The newest run of the Method that runs the comparison, since this version was saved: running, waiting for an answer,
 * stopped (failed or cancelled; reason: the run's failure in words) or finished. Null before its first run.
 */
export type ComparisonRun = { status: "running" | "waiting" | "stopped" | "finished"; reason: string | null; run_id: string; at: string };
/**
 * One question on the comparison's page. Its key is the hash of what it asks (what it shows and asks, the correction,
 * or the approval and the file), so a question that a new version does not change keeps its answer. to: creator (the
 * person who made the comparison) or workspace (any member answers; an expert's question). for: the member it names.
 * show: what the card shows before the question, in order; text: the question; detail: what a "no" means, or more.
 */
export type ComparisonQuestion = {
  key: string; kind: QuestionKind; to: "creator" | "workspace"; for: string | null;
  show: Shown[]; text: string; detail: string | null;
  choices: { choice: QuestionChoice; label: string; words: boolean }[];
};
/** An answered question: who answered, when, and the person's words. mine: the reader answered it, or made the comparison, so may take it back. */
export type ComparisonAnswer = {
  key: string; kind: QuestionKind; text: string; choice: QuestionChoice; label: string; words: string | null; by: string; at: string; mine: boolean;
};
/**
 * One run on the chart: the main measure of its grades (null until graded), and every measure. graded: a grade of the
 * run finished, so a measure without a number (a code measure that printed {"score": null}) is not measured here.
 */
export type ChartRun = {
  run_id: string | null; input: string; repeat: number; trial: boolean; graded: boolean; value: number | null; measures: Record<string, number | null>;
};
/** One point: an arm at a checkpoint (or the arm alone), the mean of its runs' main measure, and n (runs with a value). */
export type ChartPoint = {
  arm: string; checkpoint: number | null; value: number | null; n: number; planned: number; measures: Record<string, number | null>; runs: ChartRun[];
};
/** An assumption with its state: checked, failed (not true) or not checked, and what Method found when it checks it itself. */
export type AssumptionState = Assumption & { state: "checked" | "failed" | "not_checked"; detail: string | null };
export type GraderAgreement = { grader_version: string; right: number; wrong: number };
export type ComparisonVersionSummary = { version_number: number; stage: ComparisonStage; created_at: string; changed: string[] };
export type ComparisonView = {
  id: string; name: string; version_number: number; current_version_number: number;
  versions: ComparisonVersionSummary[];
  spec: ComparisonSpec; stage: ComparisonStage; locked_at: string | null; created_at: string; updated_at: string;
  trial: Trial | null;
  received: Received | null;
  cost_estimate: CostEstimate | null;
  decision: Decision | null;
  points: ChartPoint[];
  /** Run cells: planned, finished, and finished before this version (reused). */
  runs: { planned: number; finished: number; reused: number };
  /** The trial's run cells: planned and finished. */
  trial_runs: { planned: number; finished: number };
  assumptions: AssumptionState[];
  graders: GraderAgreement[];
  /** The run content stays on computers: transcripts open from the local Method server. */
  device: boolean;
  /** The workspace whose members can read the runs. */
  workspace: string;
  /** The open questions that the reader can answer, in order, and the answered questions of this version. */
  questions: ComparisonQuestion[];
  answers: ComparisonAnswer[];
  run: ComparisonRun | null;
};
export type VerdictView = GraderVerdict & { index: number; right: number; wrong: number; mine: "right" | "wrong" | null };
export type VerdictMarks = { verdict: number; right: number; wrong: number; mine: "right" | "wrong" | null };
/** marks: people's marks per verdict index, also when the verdicts themselves stay on the computer. */
export type GradeView = { key: string; grader_version: string; measures: Record<string, number>; seen: boolean; verdicts: VerdictView[]; marks: VerdictMarks[] };
export type ComparisonRunView = {
  comparison: { id: string; name: string; version_number: number; treatment_kind: string | null };
  run_id: string; local_run_id: string | null;
  /** input_name: the input's name in the file. */
  cell: { key: string; arm: string; arm_name: string; input: string; input_name: string; checkpoint: number | null; repeat: number; trial: boolean };
  device: boolean;
  treatment_note: string | null;
  /** Null when the transcript is not in the account (device mode, or not uploaded). */
  transcript: TranscriptEntry[] | null;
  grades: GradeView[];
};

/**
 * Device mode: the local Method server on the person's computer. The dashboard asks it for one run with
 * GET {LOCAL_METHOD_SERVER}/runs/{local_run_id}/comparison and reads LocalComparisonRun. It must answer the dashboard's
 * origin with CORS and Access-Control-Allow-Private-Network.
 */
export const LOCAL_METHOD_SERVER = "http://127.0.0.1:47130";
export type LocalComparisonRun = {
  treatment_note: string | null;
  transcript: TranscriptEntry[];
  /** By grade cell key: its verdicts and the text the grader read. */
  grades: Record<string, { verdicts: GraderVerdict[]; seen: string | null }>;
};
